import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { QuickAdd } from "../components/ProductCard";

export default function CapsSection() {
    const { products, loading } = useStore();
    const rail = useRef(null);
    const caps = products
        .filter((p) => p.category === "Accessories")
        .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
        .slice(0, 8);

    if (!loading && !caps.length) return null;

    const scroll = (dir) => rail.current?.scrollBy({ left: dir * (rail.current.clientWidth * 0.6), behavior: "smooth" });

    return (
        <section data-testid="caps-section" className="px-3 pb-10 sm:px-8 lg:px-16 lg:pb-24">
            <div className="mx-auto max-w-[1600px] rounded-[2rem] bg-white px-5 py-8 sm:rounded-[2.75rem] sm:px-10 sm:py-12">
                <div className="mb-8 flex items-center justify-between gap-4">
                    <h2 className="font-display text-lg font-extrabold uppercase tracking-tight text-[#0A0A0A] sm:text-2xl">
                        Vantage Caps
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="caps-prev"
                            onClick={() => scroll(-1)}
                            aria-label="Previous caps"
                            className="hidden h-9 w-9 items-center justify-center rounded-full border border-[rgba(10,10,10,0.2)] text-[#0A0A0A] transition-colors hover:bg-[#0A0A0A] hover:text-[#F2F0EA] sm:flex"
                        >
                            <ArrowLeft size={14} />
                        </button>
                        <button
                            data-testid="caps-next"
                            onClick={() => scroll(1)}
                            aria-label="Next caps"
                            className="hidden h-9 w-9 items-center justify-center rounded-full border border-[rgba(10,10,10,0.2)] text-[#0A0A0A] transition-colors hover:bg-[#0A0A0A] hover:text-[#F2F0EA] sm:flex"
                        >
                            <ArrowRight size={14} />
                        </button>
                        <Link
                            data-testid="caps-discover-more"
                            to="/shop?category=Accessories"
                            className="ml-1 whitespace-nowrap rounded-full bg-[#0A0A0A] px-5 py-2.5 text-[9px] font-bold uppercase tracking-[0.25em] text-[#F2F0EA] transition-transform duration-300 hover:scale-105 sm:text-[10px]"
                        >
                            Discover More
                        </Link>
                    </div>
                </div>
                <div ref={rail} className="flex gap-6 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:gap-10">
                    {loading
                        ? [...Array(4)].map((_, i) => (
                              <div key={i} className="w-[62vw] flex-shrink-0 sm:w-[38vw] lg:w-[23%]">
                                  <div className="mb-2 h-4" />
                                  <div className="aspect-square animate-pulse rounded-xl bg-[rgba(10,10,10,0.06)]" />
                              </div>
                          ))
                        : caps.map((p) => {
                              const soldOut = p.variants?.length ? p.variants.every((v) => !v.available) : false;
                              return (
                                  <div
                                      key={p.id}
                                      data-testid={`caps-card-${p.id}`}
                                      className="group relative w-[62vw] flex-shrink-0 sm:w-[38vw] lg:w-[23%]"
                                  >
                                      <p
                                          data-testid={soldOut ? `caps-soldout-${p.id}` : undefined}
                                          className="mb-2 h-4 text-[10px] tracking-wide text-[#6B6A66]"
                                      >
                                          {soldOut ? "Sold Out" : ""}
                                      </p>
                                      <Link to={`/product/${p.id}`} data-cursor="VIEW" className="block">
                                          <div className="relative aspect-square overflow-hidden rounded-xl bg-white">
                                              <img
                                                  src={p.front}
                                                  alt={p.name}
                                                  loading="lazy"
                                                  className="absolute inset-0 h-full w-full object-cover transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03] group-hover:opacity-0"
                                              />
                                              <img
                                                  src={p.back}
                                                  alt={`${p.name} — back`}
                                                  loading="lazy"
                                                  className="absolute inset-0 h-full w-full object-cover opacity-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03] group-hover:opacity-100"
                                              />
                                          </div>
                                          <div className="mt-3 pr-10">
                                              <p className="text-[11px] font-medium text-[#0A0A0A]">{p.name}</p>
                                              <p className="mt-0.5 text-[11px] text-[#6B6A66]">${p.price} CAD</p>
                                          </div>
                                      </Link>
                                      <QuickAdd product={p} />
                                  </div>
                              );
                          })}
                </div>
            </div>
        </section>
    );
}
